import { Fragment } from 'react';
import { Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useOrganizationsGetOrganization } from '@portfolio-manager/query';

type LinkItem = { type: 'link'; label: string; to: string; disabled?: boolean };

type ActionItem = { type: 'action'; label: string; onSelect: () => void; disabled?: boolean };

type SeparatorItem = { type: 'separator' };

export type AppLayoutItem = LinkItem | ActionItem | SeparatorItem;

export interface AppLayoutProps extends React.PropsWithChildren {
  organizationId?: string | null;
  headerItems?: AppLayoutItem[];
  footerItems?: AppLayoutItem[];
}

export function AppLayout({
  organizationId,
  headerItems = [],
  footerItems = [],
  children,
}: AppLayoutProps): React.ReactElement {
  const queryOrganization = organizationId ? useOrganizationsGetOrganization({ id: organizationId }) : false;
  return (
    <div className="flex flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b px-4 sm:px-6">
        <span className="font-semibold tracking-tight">
          {!queryOrganization && 'Portfolio Manager'}
          {queryOrganization && queryOrganization.isSuccess && queryOrganization.data.name}
        </span>
        {headerItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent align="end" sideOffset={8}>
                {headerItems.map((item, index) => (
                  <Fragment key={index}>
                    {item.type === 'link' && (
                      <DropdownMenuItem asChild disabled={item.disabled}>
                        <Link to={item.to}>{item.label}</Link>
                      </DropdownMenuItem>
                    )}
                    {item.type === 'action' && (
                      <DropdownMenuItem onSelect={item.onSelect} disabled={item.disabled}>
                        {item.label}
                      </DropdownMenuItem>
                    )}
                    {item.type === 'separator' && <Separator className="my-1" />}
                  </Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        )}
      </header>
      {children}
      {footerItems.length > 0 && (
        <footer className="flex shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t px-4 py-3 sm:px-6">
          {footerItems.map((item, index) => (
            <Fragment key={index}>
              {item.type === 'link' && !item.disabled && (
                <Link
                  to={item.to}
                  className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {item.label}
                </Link>
              )}
              {item.type === 'action' && (
                <button
                  type="button"
                  onClick={item.onSelect}
                  className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline cursor-pointer"
                  disabled={item.disabled}
                >
                  {item.label}
                </button>
              )}
              {item.type === 'separator' && <Separator orientation="vertical" />}
            </Fragment>
          ))}
        </footer>
      )}
    </div>
  );
}
